import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UIAusrichtung} from '../../../../models/uiAusrichtung';
import {BehaviorSubject, combineLatest, Observable, Subject, Subscription, switchMap} from 'rxjs';
import {debounceTime, map, tap} from 'rxjs/operators';
import {ThemeService, UiConfigService} from '../../../../../lib-core';
import {Fonds} from '../../../../models/fonds';
import {UiControl} from '../../../../models/uiControl';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {FondsService} from '../../../../services/fonds.service';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {StageConfigService} from '../../../../services/stage-config.service';

export class ModalDialogFondsInput {
  public fonds$: Observable<Fonds[]>;
  public uiControl$: Observable<UiControl>;
  public unmanagedFonds$: Observable<number[]>;
  public selectedFonds: number[];
}

enum FilterFieldNames {
  NAME = 'Name/ISIN',
  ESG = 'ESG',
  STIL = 'Stil',
  THEMA = 'Thema',
  REGION = 'Region',
  KATEGORIE = 'Kategorie',
  PAI = 'PAI',
  MINDNACHHALTIGEINVESTITIONEN = 'Mind. nachhaltige Investitionen',
  MINDOEKOLOGISCHNACHHALTIGEINVESTITIONEN = 'Mind. ökologisch nachhaltige Investitionen'
}

interface AktiverFilter {
  value: string | number;
  field: FilterFieldNames;
}

@Component({
    selector: 'lv-modal-dialog-fonds',
    templateUrl: './modal-dialog-fonds.component.html',
    styleUrls: ['./modal-dialog-fonds.component.scss'],
    standalone: false
})

export class ModalDialogFondsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('f', {static: false}) ngForm: NgForm;

  public fondsData$: Observable<number[]>;
  public nurGewaehlteFondsAnzeigen: boolean = false;
  public uiAusrichtung: UIAusrichtung;
  public filterFondsname: string;
  public filterFondskategorie: string[] = [];
  public kategorien: string[] = [];
  // ACHTUNG: Reihenfolge muss alphabetisch nach Übersetzung durch aktive-filte pipe sein - bitte hier entsprechend sortieren
  public pais: string[] = ['waste', 'biodiversity', 'socialMatters', 'greenhouseEmissions', 'water',];
  public filterSustainableInvestments: number;
  public filterSustainableInvestmentsAligned: number;
  public filterPAI: string[] = [];
  public filterRegion: string[] = [];
  public regionen: string[] = [];
  public filterThema: string[] = [];
  public themen: string[] = [];
  public filterEsgEinstufung: number[] = [];
  public esgEinstufungen: number[] = [];
  public filterInvestmentstile: string[] = [];
  public investmentStile: string[] = [];
  public displayedColumns: string[] = ['selected', 'name', 'risikoklasse', 'esgEinstufungTVO', 'factsheet', 'esgreport'];
  public selectedFonds: number[] = [];
  public filterAnzahlAktuell: number;
  public filterAnzahlGesamt: number;
  public aktiveFilter: AktiverFilter[] = [];
  public dataSource: MatTableDataSource<Fonds>;
  public filternamenSubject = new Subject<string>();
  public nurGewaehlteFondsAnzeigen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public autocomplete: string;

  @ViewChild(MatSort)
  private _sort: MatSort;
  private aktuellWaehlbareFonds: Fonds[];
  private _serachFondsSubscription = Subscription.EMPTY;

  constructor(public dialogRef: MatDialogRef<ModalDialogFondsComponent>, private _stageConfigService: StageConfigService,
              private _themeService: ThemeService,
              private _fondsService: FondsService, @Inject(MAT_DIALOG_DATA) public data: ModalDialogFondsInput) {
    this.setFilterWertemengen();
    this._serachFondsSubscription = this.filternamenSubject.pipe(
        debounceTime(300),
        switchMap(name => combineLatest([this._fondsService.getAllFondsSorted(name), this.data.unmanagedFonds$])),
        tap(combinedData => {
          const individuellWaehlbareFonds = combinedData[0].filter(einzelfonds => combinedData[1].includes(einzelfonds.fondsId));
          this.filterAnzahlAktuell = individuellWaehlbareFonds.length;
          this.aktuellWaehlbareFonds = individuellWaehlbareFonds;
          this.dataSource = new MatTableDataSource(individuellWaehlbareFonds);
          this.applyFilter();
        })
    ).subscribe();

  }

  ngAfterViewInit(): void {
    this.applySort();
  }

  public ngOnInit(): void {
    this.selectedFonds = this.data.selectedFonds;
    this.fondsData$ = combineLatest<[Fonds[], number[], boolean]>([
      this.data.fonds$, this.data.unmanagedFonds$, this.nurGewaehlteFondsAnzeigen$]).pipe(
        map(([fonds, unmanagedFonds, nurGewaehlteFondsAnzeigen]) => {
          const individuellWaehlbareFonds = fonds.filter(einzelfonds =>
              unmanagedFonds.includes(einzelfonds.fondsId) &&
              (!nurGewaehlteFondsAnzeigen || nurGewaehlteFondsAnzeigen && this.selectedFonds.includes(einzelfonds.fondsId)));
          if (!nurGewaehlteFondsAnzeigen) {
            this.filterAnzahlGesamt = individuellWaehlbareFonds.length;
          }
          this.aktuellWaehlbareFonds = individuellWaehlbareFonds;
          this.dataSource = new MatTableDataSource(individuellWaehlbareFonds);
          this.applySort();
          this.filterAnzahlAktuell = individuellWaehlbareFonds.length;
          this.setInputsEnabledState(nurGewaehlteFondsAnzeigen);
          return this.selectedFonds;
        })
    );

    this._themeService.setApplicationTheme(
        this._themeService.getThemeColor('lv-antragsannahme', '--lv-core-color-primary'),
        this._themeService.getThemeColor('lv-antragsannahme', '--lv-core-color-secondary'),
        this._themeService.getThemeColor('lv-antragsannahme', '--lv-core-color-warn'), null,
        'mat-mdc-dialog-container'
    );

    this.autocomplete = this._stageConfigService.getInputAutocomplete();
  }

  ngOnDestroy() {
    this._serachFondsSubscription.unsubscribe();
  }

  public closeDialog(): void {
    this.dialogRef.close(undefined);
  }

  public selectFonds(fondsId: number) {
    const index = this.selectedFonds.indexOf(fondsId);
    if (index >= 0) {
      this.selectedFonds.splice(index, 1);
    } else {
      this.selectedFonds.push(fondsId);
    }
  }

  applySort() {
    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): string => {
      const value = data[sortHeaderId];
      if (typeof value === 'string') {
        return value?.toLowerCase().replace('ö', 'o').replace('ü', 'u').replace('ä', 'a');
      }
      return data[sortHeaderId];
    };
    this.dataSource.sort = this._sort;
  }

  public changeInvestmentStil($event: string[]) {
    this.filterInvestmentstile = $event;
    this.applyFilter();
  }

  public changeEsgEinstufung($event: number[]) {
    this.filterEsgEinstufung = $event;
    this.applyFilter();
  }

  public changeKategorie($event: string[]) {
    this.filterFondskategorie = $event;
    this.applyFilter();
  }

  public changeRegionen($event: string[]) {
    this.filterRegion = $event;
    this.applyFilter();
  }

  public changePAI($event: string[]) {
    this.filterPAI = $event;
    this.applyFilter();
  }

  public changeThemen($event: string[]) {
    this.filterThema = $event;
    this.applyFilter();
  }

  fondsUebernehmen() {
    this.dialogRef.close(this.selectedFonds);
  }

  getLink(schriftstueckArt: string, fondsId: number): string {
    return this._fondsService.getLink(schriftstueckArt, fondsId);
  }

  changeToggle(change: MatSlideToggleChange): void {
    this.nurGewaehlteFondsAnzeigen$.next(change.checked);
  }

  public deleteAllFilter() {
    this.filterFondsname = '';
    this.filterInvestmentstile = [];
    this.filterEsgEinstufung = [];
    this.filterRegion = [];
    this.filterThema = [];
    this.filterFondskategorie = [];
    this.filterPAI = [];
    this.applyFilter();
  }

  public applyFilter() {
    this.setAktiveFilter();
    let filteredFonds: Fonds[] = this.aktuellWaehlbareFonds
    .filter(fonds => this.filterInvestmentstile.length === 0 || this.filterInvestmentstile.includes(fonds.anlageansatz))
    .filter(fonds => this.filterEsgEinstufung.length === 0 || this.filterEsgEinstufung.includes(fonds.esgEinstufungTVO))
    .filter(fonds => this.filterThema.length === 0 || this.filterThema.includes(fonds.branche))
    .filter(fonds => this.filterRegion.length === 0 || this.filterRegion.includes(fonds.region))
    .filter(fonds => this.filterFondskategorie.length === 0 || this.filterFondskategorie.includes(fonds.kategorie))
    .filter(fonds => (this.filterSustainableInvestments || 0) / 100 <= fonds.sustainableInvestments)
    .filter(fonds => (this.filterSustainableInvestmentsAligned || 0) / 100 <= fonds.sustainableInvestmentsAligned);
    this.filterPAI.forEach(pai => {
      filteredFonds = filteredFonds.filter(fonds => fonds[pai] === true
      );
    });


    this.filterAnzahlAktuell = filteredFonds.length;
    this.dataSource = new MatTableDataSource(filteredFonds);
    this.applySort();
  }

  onNewFilterName() {
    this.filternamenSubject.next(this.filterFondsname);
  }

  public deleteFilter(i: number) {
    this.removeElement(this.aktiveFilter[i]);
    this.applyFilter();
  }

  private setFilterWertemengen() {
    this.data.fonds$.subscribe(fonds => {
      this.aktuellWaehlbareFonds = fonds;
      this.filterAnzahlAktuell = fonds.length;
      fonds.forEach(fond => {
        this.investmentStile.push(fond.anlageansatz);
        this.esgEinstufungen.push(fond.esgEinstufungTVO);
        this.regionen.push(fond.region);
        this.kategorien.push(fond.kategorie);
        if (!!fond.branche) {
          this.themen.push(fond.branche);
        }
      });

      this.investmentStile = [...new Set(this.investmentStile.sort())];
      this.esgEinstufungen = [...new Set(this.esgEinstufungen.sort())];
      this.regionen = [...new Set(this.regionen.sort())];
      this.kategorien = [...new Set(this.kategorien.sort())];
      this.themen = [...new Set(this.themen.sort())];
    });
  }

  private setInputsEnabledState(disableInputs: boolean) {
    if (!!this.ngForm) {
      Object.keys((this.ngForm.form.controls)).forEach(formControlName => {
        if (disableInputs) {
          this.ngForm.form.controls[formControlName].disable();
          this.nurGewaehlteFondsAnzeigen = true;
        } else {
          this.ngForm.form.controls[formControlName].enable();
          this.nurGewaehlteFondsAnzeigen = false;
          this.applyFilter();
        }
      });
    }
  }


  private setAktiveFilter() {
    const aktiveFilter: AktiverFilter[] = [];
    if (!!this.filterFondsname) {
      aktiveFilter.push({field: FilterFieldNames.NAME, value: this.filterFondsname});
    }
    if (!!this.filterInvestmentstile.length) {
      this.filterInvestmentstile.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.STIL, value: item});
      });
    }
    if (!!this.filterEsgEinstufung.length) {
      this.filterEsgEinstufung.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.ESG, value: item});
      });
    }
    if (!!this.filterRegion.length) {
      this.filterRegion.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.REGION, value: item});
      });
    }
    if (!!this.filterThema.length) {
      this.filterThema.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.THEMA, value: item});
      });
    }
    if (!!this.filterFondskategorie.length) {
      this.filterFondskategorie.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.KATEGORIE, value: item});
      });
    }
    if (!!this.filterPAI.length) {
      this.filterPAI.forEach(item => {
        aktiveFilter.push({field: FilterFieldNames.PAI, value: item});
      });
    }
    if (this.filterSustainableInvestments > 0) {
      aktiveFilter.push({field: FilterFieldNames.MINDNACHHALTIGEINVESTITIONEN, value: this.filterSustainableInvestments + '%'});
    }
    if (this.filterSustainableInvestmentsAligned > 0) {
      aktiveFilter.push({
        field: FilterFieldNames.MINDOEKOLOGISCHNACHHALTIGEINVESTITIONEN,
        value: this.filterSustainableInvestmentsAligned + '%'
      });
    }
    this.aktiveFilter = aktiveFilter;
  }

  private removeElement(aktiverFilter: AktiverFilter) {
    switch (aktiverFilter.field) {
      case FilterFieldNames.NAME:
        this.filterFondsname = '';
        break;
      case FilterFieldNames.STIL:
        this.filterInvestmentstile = this.filterInvestmentstile.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.ESG:
        this.filterEsgEinstufung = this.filterEsgEinstufung.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.KATEGORIE:
        this.filterFondskategorie = this.filterFondskategorie.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.THEMA:
        this.filterThema = this.filterThema.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.REGION:
        this.filterRegion = this.filterRegion.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.PAI:
        this.filterPAI = this.filterPAI.filter(item => item !== aktiverFilter.value);
        break;
      case FilterFieldNames.MINDNACHHALTIGEINVESTITIONEN:
        this.filterSustainableInvestments = 0;
        break;
      case FilterFieldNames.MINDOEKOLOGISCHNACHHALTIGEINVESTITIONEN:
        this.filterSustainableInvestmentsAligned = 0;
        break;
      default:
        break;
    }
  }
}
